import React, { useCallback, useEffect, useRef } from 'react';
import UnavailableModal from '|/app/purchase/cart/steps/gap-coverage/components/unavailable-modal';
import { Dashboard, Step, StepName, Summary } from '|/app/services';
import { SomethingWentWrongModalWrapper } from '|/components/carvana-ds/something-went-wrong-modal/something-went-wrong-modal';
import { PageEventContextProvider } from '|/context';
import useEventHelper from '|/hooks/use-event-helper';
import { ReviewSummaryResponse } from '|/services/purchase-api';
import CheckingSpinner from './components/checking-spinner';
import PlaceOrderInitial from './components/place-order-initial';
import TaskTileList from './components/task-tile-list';
import TasksComplete from './components/tasks-complete';
import WeHaveEverything from './components/we-have-everything';
import useVerificationsVm from './use-verifications-vm';
import VerificationTaskSheet from './verification-task-sheet';
import styles from './verifications-panel.module.css';
import { getPageEventContext } from '../constants/page-event-mappings';

interface VerificationsPanelProps {
  verificationSteps: Step[];
  activeStepUrl: string;
  setActiveStepUrl: (url: string) => void;
  onStepComplete: () => void;
  summary: ReviewSummaryResponse | null;
  isSlotRetainedAfterRollback: boolean;
  setDashboard: (d: Dashboard) => void;
  setSummary: (s: Summary) => void;
}

const VerificationsPanelInner: React.FC<VerificationsPanelProps> = ({
  verificationSteps,
  activeStepUrl,
  setActiveStepUrl,
  onStepComplete,
  summary,
  isSlotRetainedAfterRollback,
  setDashboard,
  setSummary
}) => {
  const { sendLoadEvent, sendCompleteEvent, sendShowEvent } = useEventHelper();

  const completeFiredRef = useRef(false);
  const fireCompleteOnce = useCallback(() => {
    if (completeFiredRef.current) return;
    completeFiredRef.current = true;
    sendCompleteEvent(true);
  }, [sendCompleteEvent]);

  const shownTilesRef = useRef<Set<StepName>>(new Set());
  const handleTileShown = useCallback(
    (stepId: StepName) => {
      if (shownTilesRef.current.has(stepId)) return;
      shownTilesRef.current.add(stepId);
      sendShowEvent({ pageSubName: getPageEventContext(stepId)?.pageSubName });
    },
    [sendShowEvent]
  );

  const {
    phase,
    verificationSteps: liveSteps,
    openTaskStepId,
    partialCompletionText,
    handlePlaceOrderTap,
    handlePlaceOrderErrorDismiss,
    hasConsents,
    consentProps,
    handleTaskSubmit,
    handleSheetOpen,
    handleSheetClose,
    handleContinue: rawHandleContinue,
    isGapWaiverRemovedModalOpen,
    handleGapWaiverModalClose
  } = useVerificationsVm({
    initialVerificationSteps: verificationSteps,
    activeStepUrl,
    setActiveStepUrl,
    onStepComplete,
    onVerificationGroupComplete: fireCompleteOnce,
    summary,
    isSlotRetainedAfterRollback,
    setDashboard,
    setSummary
  });

  const loadFiredRef = useRef(false);
  useEffect(() => {
    if (loadFiredRef.current) return;
    if (phase === 'checking-consents') return;
    loadFiredRef.current = true;
    sendLoadEvent(true);
  }, [phase, sendLoadEvent]);

  const handleContinue = useCallback(() => {
    fireCompleteOnce();
    rawHandleContinue();
  }, [fireCompleteOnce, rawHandleContinue]);

  const taskSteps = liveSteps.filter(s => s.id !== StepName.PlaceOrder);

  return (
    <div className={styles.panel}>
      {phase === 'checking-consents' && <CheckingSpinner showMessage={false} />}

      {phase === 'placing-order' && <CheckingSpinner />}

      {phase === 'checking' && (
        <>
          {taskSteps.some(s => s.isComplete) && (
            <TaskTileList
              steps={taskSteps.filter(s => s.isComplete)}
              onOpenStep={handleSheetOpen}
              partialCompletionText={null}
              onTileShown={handleTileShown}
            />
          )}
          <CheckingSpinner />
        </>
      )}

      {(phase === 'place-order-initial' || phase === 'place-order-error') && (
        <PlaceOrderInitial
          onSubmit={handlePlaceOrderTap}
          hasConsents={hasConsents}
          areAgreementsComplete={consentProps.areAgreementsComplete}
          consentProps={consentProps}
          summary={summary}
        />
      )}

      <SomethingWentWrongModalWrapper
        isOpen={phase === 'place-order-error'}
        onClose={handlePlaceOrderErrorDismiss}
      />

      <UnavailableModal isOpen={isGapWaiverRemovedModalOpen} onClose={handleGapWaiverModalClose} />

      {phase === 'tasks' && (
        <TaskTileList
          steps={taskSteps}
          onOpenStep={handleSheetOpen}
          partialCompletionText={partialCompletionText}
          onTileShown={handleTileShown}
        />
      )}

      {phase === 'no-tasks' && <WeHaveEverything onContinue={handleContinue} />}

      {phase === 'tasks-complete' && (
        <div className={styles.container}>
          {taskSteps.some(s => s.isComplete) && (
            <TaskTileList
              steps={taskSteps.filter(s => s.isComplete)}
              onOpenStep={handleSheetOpen}
              partialCompletionText={null}
              onTileShown={handleTileShown}
            />
          )}
          <TasksComplete onContinue={handleContinue} />
        </div>
      )}

      {phase === 'only-kiq-complete' && (
        <div className={styles.container}>
          {taskSteps.some(s => s.isComplete) && (
            <TaskTileList
              steps={taskSteps.filter(s => s.isComplete)}
              onOpenStep={handleSheetOpen}
              partialCompletionText={null}
              onTileShown={handleTileShown}
            />
          )}
          <WeHaveEverything onContinue={handleContinue} />
        </div>
      )}

      {openTaskStepId !== null &&
        (() => {
          const openStep = liveSteps.find(s => s.id === openTaskStepId);
          if (!openStep) return null;
          return (
            <VerificationTaskSheet
              step={openStep}
              onSubmit={() => {
                void handleTaskSubmit(openTaskStepId);
              }}
              onClose={handleSheetClose}
            />
          );
        })()}
    </div>
  );
};

const VerificationsPanel: React.FC<VerificationsPanelProps> = props => {
  return (
    <PageEventContextProvider
      value={{ pageName: 'Final Details', pageSubName: null, sectionName: 'Final Details' }}
    >
      <VerificationsPanelInner {...props} />
    </PageEventContextProvider>
  );
};

export default VerificationsPanel;
